// hiddenFolders — the reserved dot-folder namespace for the content browser.
//
// A folder whose name starts with '.' is SYSTEM-RESERVED: hidden from users in
// every asset listing (folder tree, grid, global search, Move-to picker, the
// LiveTunePanel environment dropdown) and refused by the UI's create/rename
// flows. The namespace exists for machine writers — the interop e2e suite
// files its per-run org environments under '/.e2e' (tests/e2e/interop/helpers/
// runContext.js) so a leaked row (its teardown DELETE is org-admin-only and
// 403s) lands in an invisible corral instead of in front of every user.
//
// Why a name convention and not a folder allowlist: any plain-named folder a
// user makes (including a folder literally called 'e2e') behaves like every
// other folder — only the dot prefix is reserved, mirroring the dotfile
// convention users already know. The reservation is CLIENT-side only, on
// purpose: the AAM has no folder validation, and API callers are exactly who
// the hidden namespace is for.
//
// Escape hatch for debugging the suite:
//   localStorage.setItem('aos-show-hidden-folders', '1'); location.reload()

export const SHOW_HIDDEN_FOLDERS_LS_KEY = 'aos-show-hidden-folders'

// One folder-name segment. Trimmed so a rename to ' .e2e' can't dodge the rule.
export function isReservedSegment(name) {
  return typeof name === 'string' && name.trim().startsWith('.')
}

// A '/'-joined folder key as the content browser uses them ('' = root,
// 'animations/body'). Hidden when ANY segment is reserved, so '/.e2e/sub'
// stays hidden too.
export function isHiddenFolderKey(key) {
  if (typeof key !== 'string' || key === '') return false
  return key.split('/').some(isReservedSegment)
}

// A path as string[] segments (assetRegistry record `path`).
export function isHiddenFolderSegments(segments) {
  return Array.isArray(segments) && segments.some(isReservedSegment)
}

// The server's folder_path string form ('/x/y'). Root ('/' or '') is never hidden.
export function isHiddenFolderPath(folderPath) {
  if (typeof folderPath !== 'string') return false
  return isHiddenFolderKey(folderPath.replace(/^\//, ''))
}

export function showHiddenFolders() {
  try {
    return typeof localStorage !== 'undefined'
      && localStorage.getItem(SHOW_HIDDEN_FOLDERS_LS_KEY) === '1'
  } catch {
    return false
  }
}

// Validate a folder name the user typed (create / rename). Returns
// { ok: true } or { ok: false, message } — the message is user-facing.
export function validateFolderName(name) {
  if (isReservedSegment(name)) {
    return {
      ok: false,
      message: 'Folder names starting with "." are reserved for system use (hidden folders).',
    }
  }
  return { ok: true }
}
